function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import compact from 'lodash/compact';
import { TableList, Typography, DropdownMenu, TableListActions, WithInfoIcon } from 'seagull';
import { ActivityLiner24, CopyLiner24, DownloadLiner24, Edit1Liner24, EmptyWalletTickLiner24, LinkLiner24, ProposalLiner24, ReceiptEditLiner24, TrashLiner24 } from 'seagull/icons';
import { routes } from "../../constants";
import { Intent } from "../../infra/access-control";
import styles from "./AgreementsDashboardRow.module.scss";
import { relationshipFundingSourceModel, relationshipModel, relationshipSummaryModel, timeModel } from "../../models";
import EnterAccountantAccessButton from "../../components/EnterAccountantAccessButton/EnterAccountantAccessButton";
import { PlaidBankAccountStatusTypes } from "../../models/payment-method";
import { RelationshipFundingSourceType } from "../../models/relationship-funding-source";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import ShareAgreementLinksModal from "./ShareAgreementLinksModal";
import { useGlobalContext } from 'seagull';
const agreementStatusToText = {
  [relationshipSummaryModel.AgreementStatus.Active]: 'Active',
  [relationshipSummaryModel.AgreementStatus.Amended]: 'Amended',
  [relationshipSummaryModel.AgreementStatus.Exhausted]: 'Completed',
  [relationshipSummaryModel.AgreementStatus.Terminated]: 'Terminated'
};
export default function AgreementsDashboardRow(props) {
  const {
    agreement,
    isVendor,
    index,
    hasAccountantAccess,
    onVerifyPaymentMethod,
    onEditAgreement,
    onCopyDirectLink,
    onDownloadPDF,
    onDuplicateAgreement,
    onTerminateAgreement,
    onEnterAccountantAccess,
    onRequestPM,
    onRenameAgreement
  } = props;
  const {
    DIRECT_LINKS
  } = useFeatureFlags();
  const globalContext = useGlobalContext();
  const [title, setTitle] = useState(agreement.title);
  const history = useHistory();
  const opposingPartyName = relationshipSummaryModel.opposingPartyName(agreement, !!isVendor);
  const testId = agreement.isSample ? 'test-agreement' : "agreements-row-".concat(index);
  const heapId = agreement.isSample ? 'test-agreement' : 'agreement';
  const isTerminated = relationshipSummaryModel.isTerminated(agreement);
  const canCompleteVerification = relationshipSummaryModel.canCompleteVerification(agreement);
  const canRequestPM = (!agreement.fundingSource || relationshipFundingSourceModel.failedVerification(agreement.fundingSource)) && !relationshipSummaryModel.isTerminated(agreement) && isVendor;
  const agreementLink = isVendor || !relationshipModel.isPending(agreement) ? routes.agreement(agreement.id) : routes.clientOnboarding(agreement.id);
  const items = compact([{
    value: /*#__PURE__*/React.createElement("div", {
      className: styles.title
    }, /*#__PURE__*/React.createElement(Typography, {
      ellipsis: true,
      weight: "normal"
    }, opposingPartyName, title !== opposingPartyName && /*#__PURE__*/React.createElement(Typography, {
      variant: "paragraph2",
      skin: "secondary"
    }, title)), hasAccountantAccess && /*#__PURE__*/React.createElement(EnterAccountantAccessButton, {
      testid: "".concat(testId, "-enter-acc-access"),
      onClick: e => {
        e.stopPropagation(); // Prevents the row <button> element from redirecting to its target.

        onEnterAccountantAccess(relationshipSummaryModel.customerBusinessId(agreement), relationshipSummaryModel.customerBusinessName(agreement));
      }
    }))
  }, {
    value: /*#__PURE__*/React.createElement(WithInfoIcon, {
      showInfo: agreement.agreementStatus === relationshipSummaryModel.AgreementStatus.Exhausted,
      info: "All services in this agreement were completed",
      infoTestId: "".concat(testId, "-status-info")
    }, /*#__PURE__*/React.createElement(Typography, {
      skin: "secondary",
      "data-testid": "".concat(testId, "-status")
    }, agreementStatusToText[agreement.agreementStatus]))
  }, {
    value: /*#__PURE__*/React.createElement(Typography, {
      skin: "secondary"
    }, agreement.effectiveDate ? timeModel.formatDate(agreement.effectiveDate) : '')
  }, {
    value: !agreement.fundingSource ? /*#__PURE__*/React.createElement(Typography, {
      skin: "error"
    }, "Not set") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      skin: "secondary"
    }, agreement.fundingSource.type === 'creditCard' ? 'Credit card' : 'Bank account', " (", agreement.fundingSource.last4, ")"), relationshipFundingSourceModel.failedVerification(agreement.fundingSource) && /*#__PURE__*/React.createElement(Typography, {
      variant: "paragraph2",
      skin: "error"
    }, "Verification failed"), relationshipFundingSourceModel.isPendingMicroDeposit(agreement.fundingSource) && /*#__PURE__*/React.createElement(Typography, {
      variant: "paragraph2",
      skin: "warning"
    }, agreement.fundingSource.type === RelationshipFundingSourceType.Ach && agreement.fundingSource.ach.plaidVerificationStatus === PlaidBankAccountStatusTypes.PendingAutomaticVerification ? 'Automatic verification initiated' : 'Manual verification initiated'))
  }, {
    value: agreement.lastBilledDate ? /*#__PURE__*/React.createElement(Typography, {
      skin: "secondary",
      "data-testid": "".concat(testId, "-last-billed")
    }, timeModel.formatDate(agreement.lastBilledDate)) : null
  }]);

  const getButtonText = () => {
    if (canCompleteVerification) return 'Complete verification';
    return canRequestPM ? 'Request payment method' : undefined;
  };

  const onButtonClick = () => {
    if (canCompleteVerification) {
      onVerifyPaymentMethod(agreement);
    } else if (canRequestPM) {
      onRequestPM(relationshipSummaryModel.id(agreement));
    }
  };

  return /*#__PURE__*/React.createElement(TableList.Row, {
    as: 'button',
    onClick: () => {
      history.push(agreementLink);
    },
    items: items,
    rowId: agreement.id,
    "data-testid": testId,
    "data-heapid": heapId,
    actionColumn: /*#__PURE__*/React.createElement(TableListActions, {
      testId: testId,
      menuHeapId: "".concat(heapId, "-menu-btn"),
      buttonText: getButtonText(),
      secondaryButtonText: "View",
      secondaryBtnHeapId: "".concat(heapId, "-view-btn"),
      onClickButton: onButtonClick,
      onClickSecondaryButton: () => history.push(agreementLink)
    }, isVendor && !isTerminated && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      iconStart: /*#__PURE__*/React.createElement(ReceiptEditLiner24, null),
      disabled: agreement.isSample,
      "data-testid": "".concat(testId, "-edit-agreement"),
      onClick: () => onEditAgreement(agreement)
    }, "Edit agreement"), isVendor && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      iconStart: /*#__PURE__*/React.createElement(Edit1Liner24, null),
      disabled: agreement.isSample,
      "data-testid": "".concat(testId, "-rename-agreement"),
      onClick: () => onRenameAgreement(agreement, setTitle)
    }, "Rename agreement"), isVendor && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      intent: Intent.DuplicateAgreement,
      iconStart: /*#__PURE__*/React.createElement(CopyLiner24, null),
      disabled: agreement.isSample,
      "data-testid": "".concat(testId, "-duplicate"),
      "data-heapid": "".concat(heapId, "-menu-duplicate"),
      onClick: () => onDuplicateAgreement(agreement)
    }, "Duplicate agreement"), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      intent: Intent.DownloadAgreementPdf,
      iconStart: /*#__PURE__*/React.createElement(DownloadLiner24, null),
      "data-testid": "".concat(testId, "-download-pdf"),
      "data-heapid": "".concat(heapId, "-menu-download-pdf"),
      onClick: () => onDownloadPDF(agreement)
    }, "Download PDF"), DIRECT_LINKS ? /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      iconStart: /*#__PURE__*/React.createElement(LinkLiner24, null),
      "data-testid": "".concat(testId, "-direct-links"),
      onClick: () => {
        globalContext.showModal({
          closeOnClickOutside: true,
          closeOnEscapeKey: true,
          content: p => /*#__PURE__*/React.createElement(ShareAgreementLinksModal, _extends({}, p, {
            relationship: agreement
          }))
        });
      }
    }, "Share direct link") : /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      intent: Intent.CopyRelationshipDirectLink,
      iconStart: /*#__PURE__*/React.createElement(LinkLiner24, null),
      "data-testid": "".concat(testId, "-copy-direct-link"),
      "data-heapid": "".concat(heapId, "-menu-copy-direct-link"),
      onClick: () => onCopyDirectLink(agreement)
    }, "Copy link"), !isTerminated && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      as: Link,
      to: routes.billingHub(agreement.id),
      iconStart: /*#__PURE__*/React.createElement(EmptyWalletTickLiner24, null),
      disabled: agreement.isSample,
      "data-testid": "".concat(testId, "-billing-hub"),
      "data-heapid": "".concat(heapId, "-menu-billing-hub")
    }, "Manage billing"), !isTerminated && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      as: Link,
      to: routes.invoices(agreement.id),
      iconStart: /*#__PURE__*/React.createElement(ProposalLiner24, null),
      disabled: agreement.isSample,
      "data-testid": "".concat(testId, "-invoices"),
      "data-heapid": "".concat(heapId, "-menu-invoices")
    }, "View invoices"), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      as: Link,
      to: routes.activityLog(agreement.id),
      iconStart: /*#__PURE__*/React.createElement(ActivityLiner24, null),
      disabled: agreement.isSample,
      "data-testid": "".concat(testId, "-activity"),
      "data-heapid": "".concat(heapId, "-menu-activity")
    }, "View activity log"), isVendor && !isTerminated && /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      intent: Intent.TerminateAgreement,
      skin: "error",
      iconStart: /*#__PURE__*/React.createElement(TrashLiner24, null),
      "data-testid": "".concat(testId, "-terminate"),
      "data-heapid": "".concat(heapId, "-menu-terminate"),
      onClick: () => onTerminateAgreement(agreement)
    }, "Terminate agreement"))
  });
}